<template>
    <div class="kb-article-editor">
        <div class="panel h1-panel">
            <div class="panel-heading">
                <h1><router-link to="/kb">Knowledge Base</router-link></h1>
            </div>
            <div class="panel-body">
                <h2>Article Editor</h2>
            </div>
        </div>

        <section class="panel editor-panel">
            <div class="panel-body">
                <div v-if="!isLoading">
                    <h2 v-if="isCreating">Create Article</h2>
                    <h2 v-if="isUpdating">Edit Article</h2>

                    <form @submit.prevent="">
                        <fieldset :disabled="isSubmitting">
                            <div class="form-group">
                                <label>Title</label>
                                <input
                                    type="text"
                                    class="form-control"
                                    placeholder="E.g. How many polygons should I use?"
                                    v-model="title">
                            </div>

                            <div class="form-group">
                                <label>Category</label>
                                <multiselect
                                    :options="categoryOptionsAsLabels"
                                    v-model="categories">
                                </multiselect>
                            </div>

                            <div class="form-group">
                                <label>Term Type</label>
                                <multiselect
                                    class="term-type-multiselect"
                                    :options="termTypeOptions"
                                    v-model="termTypes">
                                </multiselect>
                            </div>

                            <div class="form-group">
                                <label>Path</label>
                                <multiselect
                                    :options="pathOptionsAsNames"
                                    v-model="paths">
                                </multiselect>
                            </div>

                            <div class="form-group">
                                <label>Software</label>
                                <software-select
                                    :options="softwareOptions"
                                    v-model="software">
                                </software-select>
                            </div>

                            <div class="form-group">
                                <label>Tags</label>
                                <tag-select
                                    :canCreate="true"
                                    :search="searchTags"
                                    v-model="tags">
                                </tag-select>
                            </div>

                            <div class="form-group">
                                <label>Cover Image</label>
                                <div class="cover-img-group">
                                    <img
                                        class="cover-img"
                                        :src="coverImageUrl"
                                        v-if="coverImageUrl">
                                    <img
                                        class="cover-img"
                                        :src="defaultCoverImageUrl"
                                        v-else-if="defaultCoverImageUrl">
                                    <img
                                        class="cover-img"
                                        src="../assets/img/ttto-thumbnail.jpg"
                                        v-else>
                                    <div>
                                        <label class="btn btn-default">
                                            Upload
                                            <input
                                                type="file"
                                                @change="setCoverImage($event.target.files[0])">
                                        </label>
                                        <button
                                            class="btn btn-default"
                                            @click="resetCoverImage()"
                                            v-if="coverImageUrl">
                                            Reset
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div class="form-group">
                                <label>Overview</label>
                                <input
                                    type="text"
                                    class="form-control"
                                    placeholder="E.g. Finding the balance between performance and respresentation"
                                    v-model="overview">
                            </div>

                            <div class="form-group">
                                <label>Body</label>
                                <html-editor
                                    ref="htmlEditor"
                                    contentClass="kb-article"
                                    :stickyToolbar="true"
                                    :tagMap="{ h1: 'h3', h2: 'h4' }">
                                </html-editor>
                            </div>

                            <div class="form-group submit-group">
                                <input
                                    type="submit"
                                    class="btn btn-success"
                                    :value="submitText"
                                    @click="submitArticle()">
                            </div>
                        </fieldset>
                    </form>
                </div>

                <div class="loading-msg" v-else>
                    Loading...
                </div>
            </div>
        </section>

        <section
            class="panel delete-panel"
            v-if="isUpdating">
            <div class="panel-body">
                <h2>Delete Article</h2>

                <form @submit.prevent="deleteArticle()">
                    <fieldset :disabled="isDeleting">
                        <div class="form-group">
                            <input
                                type="text"
                                class="form-control"
                                placeholder="Type &quot;delete me&quot; to enable the button below"
                                v-model="deletionText">
                        </div>
                        <div class="form-group submit-group">
                            <input
                                type="submit"
                                class="btn btn-danger"
                                value="Delete"
                                :disabled="!canDelete">
                        </div>
                    </fieldset>
                </form>
            </div>
        </section>

        <kb-feedback></kb-feedback>
    </div>
</template>

<script>
    import toastr from 'toastr';
    import UuidV4 from 'uuid/v4';

    import ThumbGenerator from '../packages/thumb-generator';
    import AwsService     from '../services/aws';
    import KbService      from '../services/knowledge-base';
    import TermUtils      from '../utils/terms';
    import HtmlEditor     from './reusable/html-editor';
    import Multiselect    from './reusable/multiselect';
    import SoftwareSelect from './reusable/software-select';
    import TagSelect      from './reusable/tag-select';
    import KbFeedback     from './widgets/KbFeedback';

    export default {
        components: {
            HtmlEditor,
            Multiselect,
            SoftwareSelect,
            TagSelect,
            KbFeedback,
        },

        data() {
            return {
                id: null,
                title: '',
                category: null,
                overview: '',
                coverImageUrl: null,
                coverImageFile: null,

                categories : [], // string[] - e.g. '3D Modeling'
                termTypes  : [], // string[] - e.g. 'foundation'
                paths      : [], // string[] - e.g. 'Modeling for Film'
                software   : [], // App[] - see app service
                tags       : [], // KbTag[] - see kb service

                isLoading: true,
                isSubmitting: false,
                isDeleting: false,
                deletionText: '',
            };
        },

        async mounted() {
            this.isLoading = true;

            try {
                await Promise.all([
                    this.$store.dispatch('ensureAllTermTypes'),
                    this.$store.dispatch('ensureKbCategories'),
                    this.$store.dispatch('ensureAllPaths'),
                    this.$store.dispatch('ensureAllApps'),
                ]);

                // If creating an article, a category may have already been
                // picked.
                if (this.isCreating) {

                    // Read the default category from the url and select it
                    if (this.$route.query.category) {
                        const qCat = this.$route.query.category;
                        const cat = this.categoryOptions.find(c => c.id == qCat);
                        if (cat) {
                            this.categories = [cat.label];
                        }
                    }

                    this.isLoading = false;
                }

                // If updating an article, load the article and select the
                // categories, term types, etc.
                if (this.isUpdating) {
                    const articleId = this.$route.params.articleId;
                    const article = await KbService.getArticle(articleId);

                    this.id            = article.id;
                    this.title         = article.title;
                    this.termTypes     = article.termTypes;
                    this.coverImageUrl = article.coverImageUrl;
                    this.overview      = article.overview;

                    this.categories = article.categories
                        .map(id => this.categoryOptions.find(c => c.id === id))
                        .map(c => c.label);

                    if (article.paths && article.paths.length) {
                        this.paths = article.paths
                            .map(id => this.pathOptions.find(p => p.id === id))
                            .map(p => p.name);
                    } else {
                        this.paths = [];
                    }

                    this.software = article.software
                        .map(id => this.softwareOptions.find(s => s.id === id));

                    await Promise
                        .all(article.tags.map(KbService.getTag))
                        .then(tags => this.tags = tags),

                    this.isLoading = false;

                    // Loading the body has to wait until the html editor has
                    // been rendered.
                    this.$nextTick(() => this.setBody(article.body));
                }

            } catch (error) {
                console.error('error loading kb article editor:', error);
                toastr.error(`
                    There was an error while loading the editor.
                    Please try again later.
                `);
            }
        },

        methods: {
            searchTags: KbService.searchTags,

            getUserId() {
                return parseInt(localStorage.user_id);
            },

            getBody() {
                return this.$refs.htmlEditor.getContent();
            },

            setBody(body) {
                this.$refs.htmlEditor.setContent(body);
            },

            setCoverImage(file) {
                this.coverImageFile = file;
                this.coverImageUrl = URL.createObjectURL(file);
            },

            resetCoverImage() {
                this.coverImageFile = null;
                this.coverImageUrl = null;
            },

            async submitArticle() {
                // Don't double-submit an article
                if (this.isSubmitting) {
                    return;
                }

                // Make sure required fields are set
                if (!this.title.trim()) {
                    toastr.error('Please specify a title.');
                    return;
                }
                if (this.categories.length < 1) {
                    toastr.error('Please specify at least one category.');
                    return;
                }
                if (!this.overview.trim()) {
                    toastr.error('Please specify a overview.');
                    return;
                }
                let body = this.getBody();
                if (!body.trim()) {
                    toastr.error('Please specify a body.');
                    return;
                }

                // Stage uploads for any images/videos used in the editor
                this.isSubmitting = true;
                const filePromises = [];
                const contentFiles = this.$refs.htmlEditor.getContentFiles();
                for (const contentFile of contentFiles) {
                    if (!body.includes(contentFile.objectUrl)) {
                        continue;
                    }
                    const filename = UuidV4();
                    const file = contentFile.object;
                    let promise;
                    if (/^image/.test(file.type)) {
                        promise = this.uploadImageToS3(filename, file);
                    } else if (/^video/.test(file.type)) {
                        promise = this.uploadVideoToS3(filename, file);
                    } else if (file.type === 'application/pdf' || file.type === 'application/zip') {
                        promise = this.uploadBinaryToS3(filename, file);
                    } else {
                        promise = Promise.reject(
                            new Error(`unknown file type: ${file.type}`)
                        );
                    }

                    promise = promise.then(url => {
                        body = body.replace(contentFile.objectUrl, url);
                    });
                    filePromises.push(promise);
                }

                // Stage upload for the cover image as well
                if (this.coverImageFile) {
                    const filename = UuidV4();
                    const file = this.coverImageFile;
                    const promise = (async () => {
                        const gen = new ThumbGenerator({
                            width: 128,
                            height: 128,
                        });
                        const thumb = await gen.genThumb(this.coverImageUrl);
                        const url = await this.uploadCoverImageToS3(
                            filename,
                            thumb,
                        );
                        URL.revokeObjectURL(this.coverImageUrl);
                        this.coverImageUrl = url;
                        this.coverImageFile = null;
                    })();
                    filePromises.push(promise);
                }

                // Upload files
                try {
                    await Promise.all(filePromises);
                } catch (err) {
                    console.log('Error uploading content files:', err, contentFiles);
                    toastr.error('There was an error while uploading the article\'s files. Please try again later.');
                    this.isSubmitting = false;
                    return;
                }

                // Update or create the article
                let id;
                try {
                    const fields = {
                        title:         this.title,
                        categories:    this.categoriesAsIds,
                        termTypes:     this.termTypes,
                        paths:         this.pathsAsIds,
                        tags:          this.tags,
                        software:      this.softwareAsIds,
                        overview:      this.overview,
                        coverImageUrl: this.coverImageUrl,
                        body:          body,
                    };

                    if (this.isUpdating) {
                        await KbService.updateArticle(this.id, fields);
                        id = this.id;
                        toastr.success('Article updated.');

                    } else {
                        fields.createdBy = this.getUserId();
                        const article = await KbService.createArticle(fields);
                        id = article.id;
                        toastr.success('Article created.');
                    }

                } catch (err) {
                    console.log('Error submitting article:', err);
                    toastr.error('There was an error while submitting the article. Please try again later.');
                    return;

                } finally {
                    this.isSubmitting = false;
                }

                // Go to the article
                this.$router.push({
                    path: '/kb-article/' + id
                });
            },

            async deleteArticle() {
                if (!this.canDelete) {
                    return;
                }
                try {
                    this.isDeleting = true;
                    await KbService.deleteArticle(this.id);
                    toastr.success('Article deleted.');
                    this.$router.push({ path: '/kb' });

                } catch (error) {
                    console.log('Error deleting article:', error);
                    toastr.error('There was an error while deleting the article. Please try again later.');

                } finally {
                    this.isDeleting = false;
                }
            },

            async uploadImageToS3(filename, file) {
                const bucket = 'ttto-kb-images-full';
                return await this.uploadFileToS3(bucket, filename, file);
            },

            async uploadCoverImageToS3(filename, file) {
                const bucket = 'ttto-kb-cover-images-thumbs';
                return await this.uploadFileToS3(bucket, filename, file);
            },

            async uploadVideoToS3(filename, file) {
                const bucket = 'ttto-kb-videos-full';
                return await this.uploadFileToS3(bucket, filename, file);
            },

            async uploadBinaryToS3(filename, file) {
                const bucket = 'ttto-kb-files';
                return await this.uploadFileToS3(bucket, filename, file);
            },

            async uploadFileToS3(bucket, filename, file) {
                const signature    = await AwsService.signature(file, filename, bucket);
                const presignedUrl = await AwsService.getPresignedUrl(this, bucket, filename, 'putObject');
                const res          = await AwsService.upload(file, filename, signature, bucket, presignedUrl.data);
                return res.url;
            },
        },

        computed: {
            /**
             * @returns {KbCategory[]} - see kb service
             */
            categoryOptions() {
                const cats = this.$store.getters.kbCategories.slice();
                return cats.sort((a, b) => a.label.localeCompare(b.label));
            },

            /**
             * @returns {string[]}
             */
            termTypeOptions() {
                const types = this.$store.getters.allTermTypes;
                return TermUtils.sortTypes(types);
            },

            /**
             * @returns {Path[]} - see path service
             */
            pathOptions() {
                return this.$store.getters.allPaths;
            },

            /**
             * @returns {App[]} - see app service
             */
            softwareOptions() {
                const apps = this.$store.getters.allApps.slice();
                return apps.sort((a, b) => a.name.localeCompare(b.name));
            },

            categoriesAsIds() {
                return this.categories.map(label => {
                    return this.categoryOptions.find(c => c.label === label).id;
                });
            },
            isCreating() {
                return !this.isUpdating;
            },

            isUpdating() {
                const id = this.$route.params.articleId;
                return !!id;
            },

            submitText() {
                if (this.isCreating) {
                    return this.isSubmitting ? 'Creating...' : 'Create';
                } else {
                    return this.isSubmitting ? 'Updating...' : 'Update';
                }
            },

            categoriesAsIds() {
                return this.categories.map(label => {
                    return this.categoryOptions.find(c => c.label === label).id;
                });
            },

            pathsAsIds() {
                return this.paths.map(name => {
                    return this.pathOptions.find(p => p.name === name).id;
                });
            },

            softwareAsIds() {
                return this.software.map(s => s.id);
            },

            categoryOptionsAsLabels() {
                return this.categoryOptions.map(c => c.label);
            },

            pathOptionsAsNames() {
                return this.pathOptions.map(p => p.name);
            },

            canDelete() {
                return !this.isDeleting && this.deletionText === 'delete me';
            },

            defaultCoverImageUrl() {
                if (this.categories.length > 0) {
                    const c = this.categoryOptions.find(c => {
                        return c.label === this.categories[0];
                    });
                    return c.imageUrl;
                }
                return null;
            },
        },
    }
</script>

<style lang="scss" scoped>
    .h1-panel {
        display: flex;

        .panel-heading,
        .panel-body {
            height: auto;
            width: auto;
            display: flex;
            align-items: center;
        }

        h1 {
            margin: 10px 0;

            a {
                color: inherit;
                text-decoration: none;
                &:hover {
                    color: #33ccff;
                }
            }
        }

        h2 {
            margin: 10px 15px;
            font-size: 20px;
        }
    }

    .editor-panel {
        .panel-body {
            padding: 40px 20px;
            max-width: 800px;
            margin: 0 auto;
        }

        h2 {
            margin: 0 0 40px 0;
        }

        .form-group {
            margin-bottom: 40px;

            &.submit-group {
                margin-bottom: 10px;
            }
        }

        .cover-img-group {
            padding-top: 10px;
            > * {
                display: inline-block;
                vertical-align: top;
                margin-right: 10px;
            }
            .cover-img {
                width: 128px;
                height: 128px;
                object-fit: cover;
                border: 2px solid #565656;
            }
            .btn {
                display: block;
                width: 100%;
                font-weight: 400 !important;
                margin-bottom: 10px;
            }
            label {
                position: static;
            }
            input[type=file] {
                display: none;
            }
        }

        .html-editor {
            margin: 10px 0 20px;
        }

        .loading-msg {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100px;
            width: 100%;
            font-style: italic;
        }
    }

    .delete-panel {
        .panel-body {
            padding: 40px 20px;
            max-width: 800px;
            margin: 0 auto;
        }

        h2 {
            margin: 0 0 40px 0;
        }

        .form-group {
            margin-bottom: 40px;

            &.submit-group {
                margin-bottom: 10px;
            }
        }
    }
</style>

<style lang="scss">
    .kb-article-editor {
        .editor-panel {
            .multiselect {
                margin-bottom: -15px;
            }

            .term-type-multiselect button {
                text-transform: capitalize;
            }

            .software-select {
                .software-list {
                    margin-top: 20px;

                    > li {
                        width: 100px;
                    }
                }
            }
        }

        .kb-feedback-panel {
            max-width: 800px;
            margin: 0 auto 30px;
        }
    }
</style>



// WEBPACK FOOTER //
// KbArticleEditor.vue?5c8e909d